import React, { useState, useEffect, useRef } from 'react';
import {
  Modal,
  Stack,
  Text,
  Button,
  Group,
  Select,
  Flex,
  Loader,
  Alert,
  Code,
  Divider,
  Badge,
  CopyButton,
  Tooltip,
  ActionIcon,
  Table,
  ScrollArea,
  Tabs,
  SegmentedControl,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconCheck,
  IconCopy,
  IconDownload,
  IconMail,
} from '@tabler/icons-react';
import { extractEmailsFromThread, groupEmails, emailsToCSV, emailsWithContextToCSV } from '../utils/emailExtraction';
import { trackEvent } from '../utils/analytics';

/**
 * 이메일 추출 모달 컴포넌트
 *
 * Props:
 * @param {boolean} opened - 모달 열림 상태 [Required]
 * @param {function} onClose - 모달 닫기 핸들러 [Required]
 * @param {string} threadId - 스레드 ID [Required]
 * @param {string} threadText - 스레드 내용 (미리보기용) [Optional]
 *
 * Example usage:
 * <EmailExtractionModal
 *   opened={modalOpened}
 *   onClose={() => setModalOpened(false)}
 *   threadId={thread.id}
 *   threadText={thread.text}
 * />
 */
const EmailExtractionModal = ({ opened, onClose, threadId, threadText = '' }) => {
  // States
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0, message: '' });
  const [emails, setEmails] = useState([]);
  const [emailsWithContext, setEmailsWithContext] = useState([]);
  const [repliesCount, setRepliesCount] = useState(0);
  const [error, setError] = useState(null);
  const [groupSize, setGroupSize] = useState('20');
  const [activeTab, setActiveTab] = useState('simple');
  const [extractionMode, setExtractionMode] = useState('basic'); // basic | deep
  const latestRequestRef = useRef(null);

  // 모달이 열릴 때 이메일 추출 시작
  useEffect(() => {
    if (opened && threadId) {
      extractEmails(extractionMode);
    } else {
      // 모달이 닫히면 상태 초기화
      resetState();
    }
  }, [opened, threadId, extractionMode]);

  const resetState = () => {
    setIsLoading(false);
    setProgress({ current: 0, total: 0, message: '' });
    setEmails([]);
    setEmailsWithContext([]);
    setRepliesCount(0);
    setError(null);
    setActiveTab('simple');
    setExtractionMode('basic');
    latestRequestRef.current = null;
  };

  const extractEmails = async (modeOverride = extractionMode) => {
    const requestId = `${threadId || 'unknown'}-${modeOverride}-${Date.now()}`;
    latestRequestRef.current = requestId;
    setIsLoading(true);
    setError(null);
    setProgress({ current: 0, total: 0, message: 'Starting...' });
    const includeNested = modeOverride === 'deep';

    try {
      const result = await extractEmailsFromThread(threadId, (current, total, message) => {
        setProgress({ current, total, message: message || '' });
      }, { includeNested });

      if (latestRequestRef.current !== requestId) {
        return;
      }

      setEmails(result.emails);
      setEmailsWithContext(result.emailsWithContext);
      setRepliesCount(result.repliesCount);

      trackEvent('email_extraction_success', {
        threadId,
        emailsFound: result.emails.length,
        repliesScanned: result.repliesCount,
        mode: modeOverride
      });
    } catch (err) {
      if (latestRequestRef.current !== requestId) {
        return;
      }
      console.error('Error extracting emails:', err);
      setError(err.message || '이메일을 추출하는 중 오류가 발생했습니다.');
      trackEvent('email_extraction_error', { threadId, error: err.message, mode: modeOverride });
    } finally {
      if (latestRequestRef.current === requestId) {
        setIsLoading(false);
      }
    }
  };

  // CSV 다운로드 핸들러
  const handleDownloadCSV = (withContext = false) => {
    const csvContent = withContext
      ? emailsWithContextToCSV(emailsWithContext)
      : emailsToCSV(emails);

    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    const fileName = withContext
      ? `emails_with_context_${threadId}.csv`
      : `emails_${threadId}.csv`;

    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    trackEvent('download_emails_csv', {
      threadId,
      count: emails.length,
      withContext
    });
  };

  // 그룹화된 이메일 생성
  const groupedEmails = groupEmails(emails, parseInt(groupSize));

  // 복사용 텍스트 생성
  const getCopyText = (group) => {
    return group.join(', ');
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="xl"
      title={
        <Group gap="xs">
          <IconMail size={24} />
          <Text size="lg" fw={600}>이메일 추출</Text>
        </Group>
      }
      centered
    >
      <Stack gap="md">
        {/* 스레드 미리보기 */}
        {threadText && (
          <Alert variant="light" color="blue" icon={<IconAlertCircle size={16} />}>
            <Text size="sm" lineClamp={2}>
              {threadText}
            </Text>
          </Alert>
        )}

        {/* 추출 모드 선택 */}
        <Stack gap={4}>
          <Group justify="space-between" align="center">
            <Text size="sm" fw={500}>추출 모드</Text>
            <SegmentedControl
              value={extractionMode}
              onChange={setExtractionMode}
              data={[
                { label: '기본 (댓글)', value: 'basic' },
                { label: '깊은 추출 (대댓글 포함)', value: 'deep' },
              ]}
            />
          </Group>
          {extractionMode === 'deep' && (
            <Text size="xs" c="dimmed">
              대댓글까지 확인하므로 시간이 조금 더 걸릴 수 있습니다.
            </Text>
          )}
        </Stack>

        {/* 로딩 상태 */}
        {isLoading && (
          <Stack gap="sm" align="center" py="xl">
            <Loader size="lg" />
            <Text size="sm" c="dimmed">
              {progress.message || '댓글을 분석하는 중...'}
            </Text>
            {progress.total > 0 && (
              <Text size="xs" c="dimmed">
                ({progress.current}/{progress.total})
              </Text>
            )}
          </Stack>
        )}

        {/* 에러 상태 */}
        {error && !isLoading && (
          <Alert variant="light" color="red" icon={<IconAlertCircle size={16} />}>
            <Text size="sm">{error}</Text>
            <Button
              size="xs"
              variant="subtle"
              mt="xs"
              onClick={extractEmails}
            >
              다시 시도
            </Button>
          </Alert>
        )}

        {/* 결과 표시 */}
        {!isLoading && !error && emails.length > 0 && (
          <>
            {/* 통계 정보 */}
            <Group gap="md">
              <Badge size="lg" variant="light" color="blue">
                총 댓글: {repliesCount}개
              </Badge>
              <Badge size="lg" variant="light" color="green">
                이메일: {emails.length}개
              </Badge>
            </Group>

            <Divider />

            {/* 탭 */}
            <Tabs value={activeTab} onChange={setActiveTab}>
              <Tabs.List>
                <Tabs.Tab value="simple">
                  간단히 보기
                </Tabs.Tab>
                <Tabs.Tab value="detailed">
                  상세 정보
                </Tabs.Tab>
              </Tabs.List>

              {/* 간단히 보기 탭 */}
              <Tabs.Panel value="simple" pt="md">
                <Stack gap="md">
                  {/* 그룹 크기 선택 */}
                  <Group gap="md">
                    <Text size="sm" fw={500}>그룹 크기:</Text>
                    <Select
                      value={groupSize}
                      onChange={setGroupSize}
                      data={[
                        { value: '10', label: '10개씩' },
                        { value: '20', label: '20개씩' },
                        { value: '50', label: '50개씩' },
                        { value: '100', label: '100개씩' },
                        { value: String(emails.length), label: '전체' },
                      ]}
                      style={{ width: 120 }}
                    />
                  </Group>

                  {/* 그룹화된 이메일 목록 */}
                  <ScrollArea h={300}>
                    <Stack gap="md">
                      {groupedEmails.map((group, idx) => (
                        <div key={idx}>
                          <Flex justify="space-between" align="center" mb="xs">
                            <Text size="sm" fw={500}>
                              그룹 {idx + 1} ({group.length}개)
                            </Text>
                            <CopyButton value={getCopyText(group)} timeout={2000}>
                              {({ copied, copy }) => (
                                <Tooltip label={copied ? '복사됨!' : '복사'} withArrow>
                                  <ActionIcon
                                    color={copied ? 'teal' : 'gray'}
                                    variant="subtle"
                                    onClick={copy}
                                  >
                                    {copied ? (
                                      <IconCheck size={16} />
                                    ) : (
                                      <IconCopy size={16} />
                                    )}
                                  </ActionIcon>
                                </Tooltip>
                              )}
                            </CopyButton>
                          </Flex>
                          <Code block p="sm" style={{ fontSize: '12px' }}>
                            {getCopyText(group)}
                          </Code>
                        </div>
                      ))}
                    </Stack>
                  </ScrollArea>
                </Stack>
              </Tabs.Panel>

              {/* 상세 정보 탭 */}
              <Tabs.Panel value="detailed" pt="md">
                <ScrollArea h={400}>
                  <Table highlightOnHover>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>이메일</Table.Th>
                        <Table.Th>사용자명</Table.Th>
                        <Table.Th>댓글 내용</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {emailsWithContext.map((item, idx) => (
                        <Table.Tr key={idx}>
                          <Table.Td>
                            <Code>{item.email}</Code>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm">@{item.username}</Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm" lineClamp={2}>
                              {item.text}
                            </Text>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </ScrollArea>
              </Tabs.Panel>
            </Tabs>

            <Divider />

            {/* 액션 버튼 */}
            <Group justify="flex-end" gap="md">
              <CopyButton value={emails.join('\n')} timeout={2000}>
                {({ copied, copy }) => (
                  <Button
                    leftSection={copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                    variant="light"
                    color={copied ? 'teal' : 'blue'}
                    onClick={copy}
                  >
                    {copied ? '복사됨!' : '전체 복사'}
                  </Button>
                )}
              </CopyButton>
              <Button
                leftSection={<IconDownload size={16} />}
                variant="light"
                onClick={() => handleDownloadCSV(false)}
              >
                CSV 다운로드
              </Button>
              <Button
                leftSection={<IconDownload size={16} />}
                variant="filled"
                onClick={() => handleDownloadCSV(true)}
              >
                상세 CSV 다운로드
              </Button>
            </Group>
          </>
        )}

        {/* 결과가 없는 경우 */}
        {!isLoading && !error && emails.length === 0 && repliesCount >= 0 && (
          <Alert variant="light" color="yellow" icon={<IconAlertCircle size={16} />}>
            <Text size="sm">
              {repliesCount === 0
                ? '이 스레드에는 댓글이 없습니다.'
                : `${repliesCount}개의 댓글을 확인했지만 이메일 주소를 찾지 못했습니다.`
              }
            </Text>
          </Alert>
        )}
      </Stack>
    </Modal>
  );
};

export default EmailExtractionModal;
